import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Search, Phone, ArrowUpRight, MapPin, Mail, Clock } from "lucide-react";
import { NAV, COMPANY } from "../../data/site";
import { EnquiryForm } from "./bits";

const LANGS = [
  { label: "English", slug: "english" },
  { label: "العربية", slug: "arabic" },
  { label: "فارسی", slug: "farsi" },
  { label: "پښتو", slug: "pashto" },
  { label: "Français", slug: "french" },
  { label: "Українська", slug: "ukrainian" },
  { label: "Kurdî", slug: "kurdish" },
  { label: "Soomaali", slug: "somali" },
  { label: "Tigrinya", slug: "tigrinya" },
];

function LangSelector() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("English");
  return (
    <div className="relative">
      <button data-testid="lang-toggle" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-sm text-brand-dark/80 transition-colors hover:text-brand-green">
        <Globe className="h-4 w-4" /> <span className="hidden lg:inline">{cur}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            className="absolute right-0 mt-3 w-44 rounded-2xl border border-brand-dark/10 bg-white p-2 shadow-xl z-50">
            {LANGS.map((l) => (
              <button key={l.slug} data-testid={`lang-${l.slug}`} onClick={() => { setCur(l.label); setOpen(false); }}
                className="block w-full rounded-lg px-3 py-2 text-left text-sm text-brand-slate transition-colors hover:bg-brand-main hover:text-brand-green">{l.label}</button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SearchBox() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const results = q ? NAV.filter((n) => n.label.toLowerCase().includes(q.toLowerCase())) : NAV;
  return (
    <>
      <button data-testid="search-toggle" onClick={() => setOpen(true)} className="text-brand-dark/80 transition-colors hover:text-brand-green"><Search className="h-4 w-4" /></button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}
            className="fixed inset-0 z-[100] flex items-start justify-center bg-brand-dark/60 backdrop-blur-sm p-6 pt-32">
            <motion.div initial={{ y: -20 }} animate={{ y: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
              <div className="flex items-center gap-3 border-b border-brand-dark/10 pb-4">
                <Search className="h-5 w-5 text-brand-green" />
                <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…" className="w-full bg-transparent text-lg text-brand-dark focus:outline-none" />
                <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-brand-slate" /></button>
              </div>
              <div className="mt-4 space-y-1">
                {results.map((r) => (
                  <Link key={r.to} to={r.to} onClick={() => setOpen(false)} className="flex items-center justify-between rounded-xl px-4 py-3 text-brand-dark transition-colors hover:bg-brand-main">
                    {r.label} <ArrowUpRight className="h-4 w-4 text-brand-green" />
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${scrolled ? "bg-brand-main/85 backdrop-blur-xl shadow-[0_1px_0_rgba(10,31,44,0.08)]" : "bg-transparent"}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={`${process.env.PUBLIC_URL}/shelterease-logo.png`} alt="Shelterease Housing Association" className="h-11 w-auto md:h-12" />
        </Link>
        <nav className="hidden items-center gap-6 xl:flex">
          {NAV.slice(0, 8).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className={({ isActive }) => `link-underline text-sm transition-colors ${isActive ? "text-brand-green" : "text-brand-dark/80 hover:text-brand-dark"}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-4"><SearchBox /><LangSelector /></div>
          <Link to="/referrals" data-testid="nav-cta" className="hidden sm:inline-flex rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-brand-dark">Make a Referral</Link>
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="xl:hidden text-brand-dark"><Menu className="h-6 w-6" /></button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-brand-dark text-brand-main xl:hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <span className="font-serif text-lg">Menu</span>
              <button data-testid="menu-close" onClick={() => setOpen(false)}><X className="h-6 w-6" /></button>
            </div>
            <nav className="flex flex-col px-6 py-4">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.to.slice(1)}`} className="block border-b border-white/10 py-4 font-serif text-2xl">{n.label}</Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pt-6"><LangSelector /></div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-brand-dark text-brand-main">
      <div className="mx-auto max-w-7xl px-6 pt-24 pb-12">
        <h2 className="font-serif leading-[0.9] tracking-tight text-[15vw] md:text-[11vw]">Let's Build<br /><span className="italic text-brand-leaf">Strong Futures.</span></h2>
        <div className="mt-16 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="max-w-sm text-brand-main/70">Join our newsletter for community stories, service updates and opportunities to get involved.</p>
            <div className="mt-6 max-w-sm">
              <EnquiryForm kind="newsletter" testid="newsletter-form" submitLabel="Subscribe" fields={[{ name: "email", label: "Email address", type: "email", required: true, placeholder: "you@email.com" }]} />
            </div>
          </div>
          <div className="md:col-span-3 md:col-start-7">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-leaf">Explore</p>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => <li key={n.to}><Link to={n.to} className="link-underline text-sm text-brand-main/75 hover:text-white">{n.label}</Link></li>)}
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-leaf">Contact</p>
            <ul className="mt-5 space-y-4 text-sm text-brand-main/75">
              <li className="flex gap-3"><MapPin className="h-4 w-4 shrink-0 text-brand-leaf" />{COMPANY.address}</li>
              <li className="flex gap-3"><Phone className="h-4 w-4 shrink-0 text-brand-leaf" />{COMPANY.phone}</li>
              <li className="flex gap-3"><Mail className="h-4 w-4 shrink-0 text-brand-leaf" />{COMPANY.email}</li>
              <li className="flex gap-3"><Clock className="h-4 w-4 shrink-0 text-brand-leaf" />{COMPANY.hours}</li>
            </ul>
            <div className="mt-6 flex gap-4">
              {COMPANY.socials.map((s) => <a key={s} href="#" className="text-xs text-brand-main/60 hover:text-brand-leaf">{s}</a>)}
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-brand-main/50 md:flex-row md:items-center md:justify-between">
          <p>{COMPANY.reg}</p>
          <div className="flex flex-wrap gap-5">
            <Link to="/privacy" className="hover:text-brand-leaf">Privacy Policy</Link>
            <Link to="/accessibility" className="hover:text-brand-leaf">Accessibility</Link>
            <span>© {new Date().getFullYear()} Shelterease Supported Exempted Accommodation</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => { if (!localStorage.getItem("se-cookie")) setShow(true); }, []);
  if (!show) return null;
  const dismiss = (v) => { localStorage.setItem("se-cookie", v); setShow(false); };
  return (
    <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 z-[90] mx-auto max-w-2xl rounded-2xl border border-brand-dark/10 bg-white p-6 shadow-2xl md:left-6 md:right-auto">
      <p className="text-sm text-brand-slate">We use cookies to improve your experience and understand how our site is used. See our <Link to="/privacy" className="text-brand-green underline">Privacy Policy</Link>.</p>
      <div className="mt-4 flex gap-3">
        <button data-testid="cookie-accept" onClick={() => dismiss("all")} className="rounded-full bg-brand-green px-5 py-2 text-sm font-semibold text-white hover:bg-brand-dark transition-colors">Accept all</button>
        <button data-testid="cookie-reject" onClick={() => dismiss("essential")} className="rounded-full border border-brand-dark/20 px-5 py-2 text-sm text-brand-dark hover:border-brand-green transition-colors">Essential only</button>
      </div>
    </motion.div>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="App relative min-h-screen bg-brand-main">
      <div className="noise-overlay" />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
